import { FC } from "react";

import { RecordTree } from "@tsmono/inspect-components/content";
import {
  LabeledValue,
  MarkdownDivWithReferences,
  MarkdownReference,
} from "@tsmono/react/components";
import { isRecord } from "@tsmono/util";

import styles from "./Metadata.module.css";

interface MetadataProps {
  metadata: Record<string, unknown>;
  references?: MarkdownReference[];
  options?: {
    previewRefsOnHover?: boolean;
  };
  /** Keys to omit from the rendered metadata dump (promoted + directly excluded). */
  excludeKeys?: readonly string[];
}

export const Metadata: FC<MetadataProps> = ({
  metadata,
  references,
  options,
  excludeKeys,
}) => {
  const excluded = excludeKeys ? new Set(excludeKeys) : null;
  const entries = Object.entries(metadata).filter(
    ([key]) => !excluded?.has(key)
  );
  if (entries.length === 0) {
    return null;
  }

  return (
    <>
      {entries.map(([key, value]) => (
        <LabeledValue key={key} label={key} className={styles.entry}>
          <MetadataValue
            id={key}
            value={value}
            references={references}
            options={options}
          />
        </LabeledValue>
      ))}
    </>
  );
};

interface MetadataValueProps {
  id: string;
  value: unknown;
  references?: MarkdownReference[];
  options?: {
    previewRefsOnHover?: boolean;
  };
}

export const MetadataValue: FC<MetadataValueProps> = ({
  id,
  value,
  references,
  options,
}) => {
  switch (typeof value) {
    case "string":
      return (
        <MarkdownDivWithReferences
          markdown={value}
          references={references}
          options={options}
        />
      );
    case "object": {
      if (value === null) {
        return <code>null</code>;
      }
      if (Array.isArray(value)) {
        const record: Record<string, unknown> = {};
        value.forEach((item, i) => {
          record[`[${i}]`] = item;
        });
        return (
          <RecordTree
            id={`metadata-${id}`}
            record={record}
            useBorders={false}
            copyButton={true}
          />
        );
      }
      if (isRecord(value)) {
        return (
          <RecordTree
            id={`metadata-${id}`}
            record={value}
            useBorders={false}
            copyButton={true}
          />
        );
      }
      // Unreachable: a non-null, non-array `typeof "object"` always satisfies
      // isRecord. Present so the narrowing is exhaustive, not as a case.
      return null;
    }
    // At this point value is a primitive (number, boolean, undefined, bigint, symbol).
    default:
      return <span>{String(value)}</span>;
  }
};
